import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../../services/projectService';

const FeaturedProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await projectService?.getActiveProjects();
      setProjects(data?.slice(0, 3) || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAllProjects = () => {
    navigate('/projects');
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16 lg:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading projects...</p>
          </div>
        </div>
      </section>
    );
  }

  if (projects?.length === 0) {
    return null;
  }

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12 lg:mb-16">
          <span className="inline-block px-4 py-2 bg-accent/10 text-accent rounded-full text-sm md:text-base font-medium mb-4">
            Projects
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Featured Projects
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            Showcasing impactful solutions that drive business results
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-8 md:mb-12">
          {projects?.map((project) =>
          <div
            key={project?.id}
            className="group bg-card rounded-xl md:rounded-2xl overflow-hidden border border-border hover:border-accent/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-2">

              <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
                <Image
                src={project?.image}
                alt={project?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-accent text-accent-foreground text-xs md:text-sm rounded-full font-medium">
                    {project?.category}
                  </span>
                </div>
              </div>

              <div className="p-6 md:p-8">
                <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2 group-hover:text-accent transition-colors duration-300">
                  {project?.title}
                </h3>

                <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-3">
                  {project?.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {project?.technologies?.map((tech, index) =>
                <span
                  key={index}
                  className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md border border-border">

                      {tech}
                    </span>
                )}
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <div className="text-base md:text-lg font-semibold text-foreground">
                      {project?.metricsUsers}
                    </div>
                    <div className="text-xs text-muted-foreground">Users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-base md:text-lg font-semibold text-foreground">
                      {project?.metricsPerformance}
                    </div>
                    <div className="text-xs text-muted-foreground">Uptime</div>
                  </div>
                  <div className="text-center">
                    <div className="text-base md:text-lg font-semibold text-foreground">
                      {project?.metricsRating}
                    </div>
                    <div className="text-xs text-muted-foreground">Rating</div>
                  </div>
                </div>

                <Button
                variant="outline"
                size="sm"
                iconName="ArrowRight"
                iconPosition="right"
                fullWidth
                className="group-hover:bg-accent group-hover:text-accent-foreground group-hover:border-accent"
                onClick={() => {
                  if (project?.link) {
                    window.open(project?.link, '_blank', 'noopener,noreferrer');
                  } else {
                    alert('Project details coming soon!');
                  }
                }}>

                  View Details
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center">
          <Button
            variant="default"
            size="lg"
            iconName="Grid"
            iconPosition="left"
            onClick={handleViewAllProjects}>

            View All Projects
          </Button>
        </div>
      </div>
    </section>);

};

export default FeaturedProjects;